import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, ArrowUpRight, CaretDown, ArrowRight } from "@phosphor-icons/react";
import PhosphorIcon from "../lib/phosphor-icons";
import { BRAND, SERVICES } from "../lib/constants";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", hasDropdown: true },
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

// Service categories mirroring an Infosys-style grouping
const SERVICE_CATEGORIES = [
  {
    title: "Engineering",
    subtitle: "Build & ship software",
    slugs: ["web-development", "app-development", "product-development"],
  },
  {
    title: "Intelligent Tech",
    subtitle: "AI, automation, robotics",
    slugs: ["artificial-intelligence", "robotics", "data-analytics"],
  },
  {
    title: "Healthcare IT",
    subtitle: "HIPAA-compliant RCM",
    slugs: ["medical-coding", "medical-billing"],
  },
  {
    title: "Growth & Marketing",
    subtitle: "Pipeline that compounds",
    slugs: ["digital-marketing"],
  },
  {
    title: "Enterprise Ops",
    subtitle: "Infrastructure & operations",
    slugs: ["cloud", "it-support", "it-consulting-amc", "crm-erp", "it-outsourcing"],
  },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  function handleServicesEnter() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  }
  function handleServicesLeave() {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 140);
  }

  const serviceBySlug = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt="Gtechnohubb" className="h-11 w-11 rounded-md object-cover ring-1 ring-white/10 group-hover:ring-brand-mint/60 transition" />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-lg text-white tracking-tight">GTECHNOHUBB</div>
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-brand-mint/80">New Gen Technology</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) =>
            l.hasDropdown ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleServicesLeave}
              >
                <button
                  data-testid="nav-services-trigger"
                  onClick={() => setServicesOpen((v) => !v)}
                  className={`nav-link text-sm font-medium tracking-wide uppercase transition-colors flex items-center gap-1.5 ${
                    location.pathname.startsWith("/services") ? "text-brand-mint active" : "text-white/80 hover:text-white"
                  }`}
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                >
                  {l.label}
                  <CaretDown
                    size={12}
                    weight="bold"
                    className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium tracking-wide uppercase transition-colors ${
                    isActive ? "text-brand-mint active" : "text-white/80 hover:text-white"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book-consultation"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-2 bg-brand-mint text-brand-navy-darker hover:bg-brand-mint-light transition rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Book Consultation
            <ArrowUpRight size={16} weight="bold" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Desktop wide mega dropdown for Services */}
      <AnimatePresence>
        {servicesOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onMouseEnter={handleServicesEnter}
            onMouseLeave={handleServicesLeave}
            data-testid="services-mega-menu"
            className="hidden lg:block absolute left-0 right-0 top-full pt-2"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="rounded-3xl border border-white/10 bg-brand-navy-darker shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden">
                {/* Header row */}
                <div className="px-8 py-5 border-b border-white/8 flex items-center justify-between">
                  <div>
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-brand-mint mb-1">
                      / Our Services
                    </div>
                    <div className="font-display text-xl text-white tracking-tight">
                      Fourteen capabilities. One partner.
                    </div>
                  </div>
                  <Link
                    to="/services"
                    data-testid="nav-services-viewall"
                    className="inline-flex items-center gap-2 text-brand-mint hover:text-brand-mint-light text-xs font-mono-accent uppercase tracking-[0.22em] transition"
                  >
                    View all services <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>

                {/* Categories grid */}
                <div className="grid grid-cols-5 divide-x divide-white/5">
                  {SERVICE_CATEGORIES.map((cat) => (
                    <div
                      key={cat.title}
                      className="px-7 py-7"
                      data-testid={`nav-category-${cat.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="mb-5">
                        <div className="font-display text-white text-base tracking-tight leading-tight">
                          {cat.title}
                        </div>
                        <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-brand-slate mt-1">
                          {cat.subtitle}
                        </div>
                      </div>
                      <ul className="space-y-1">
                        {cat.slugs.map((slug) => {
                          const s = serviceBySlug[slug];
                          if (!s) return null;
                          return (
                            <li key={slug}>
                              <Link
                                to={`/services/${slug}`}
                                data-testid={`nav-dropdown-service-${slug}`}
                                className="group flex items-center gap-2.5 py-2 text-white/80 hover:text-brand-mint transition"
                              >
                                <PhosphorIcon
                                  name={s.icon}
                                  size={15}
                                  weight="duotone"
                                  className="text-brand-mint/70 group-hover:text-brand-mint shrink-0"
                                />
                                <span className="text-[13px] font-medium tracking-tight leading-snug flex-1">
                                  {s.title}
                                </span>
                                <ArrowRight
                                  size={11}
                                  weight="bold"
                                  className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-brand-mint"
                                />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Footer CTA strip */}
                <div className="border-t border-white/8 px-8 py-4 flex items-center justify-between bg-brand-navy/40">
                  <div className="text-sm text-brand-slate">
                    Not sure which service you need?
                  </div>
                  <Link
                    to="/book-consultation"
                    data-testid="nav-dropdown-cta"
                    className="inline-flex items-center gap-2 bg-brand-mint text-brand-navy-darker hover:bg-brand-mint-light transition rounded-full px-5 py-2 text-xs font-semibold"
                  >
                    Book a free consultation <ArrowUpRight size={14} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden glass-nav border-t border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
              {links.map((l) =>
                l.hasDropdown ? (
                  <div key={l.to}>
                    <button
                      data-testid="mobile-nav-services-toggle"
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      className="w-full flex items-center justify-between py-3 text-lg font-display tracking-tight text-white/90"
                    >
                      <span className={location.pathname.startsWith("/services") ? "text-brand-mint" : ""}>
                        {l.label}
                      </span>
                      <CaretDown
                        size={16}
                        weight="bold"
                        className={`transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-3 pb-3">
                            <Link
                              to="/services"
                              data-testid="mobile-nav-services-all"
                              className="flex items-center gap-2 py-2 text-sm text-brand-mint font-mono-accent uppercase tracking-[0.18em]"
                            >
                              <ArrowRight size={12} weight="bold" /> All services
                            </Link>
                            {SERVICE_CATEGORIES.map((cat) => (
                              <div key={cat.title} className="mt-3">
                                <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-brand-slate mb-1.5">
                                  {cat.title}
                                </div>
                                {cat.slugs.map((slug) => {
                                  const s = serviceBySlug[slug];
                                  if (!s) return null;
                                  return (
                                    <Link
                                      key={slug}
                                      to={`/services/${slug}`}
                                      data-testid={`mobile-nav-service-${slug}`}
                                      className="flex items-center gap-3 py-2 rounded-lg hover:bg-brand-mint/10 px-2 -mx-2 transition"
                                    >
                                      <PhosphorIcon name={s.icon} size={15} weight="duotone" className="text-brand-mint" />
                                      <span className="text-sm text-white/85">{s.title}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `py-3 text-lg font-display tracking-tight ${
                        isActive ? "text-brand-mint" : "text-white/90"
                      }`
                    }
                    end={l.to === "/"}
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <Link
                to="/book-consultation"
                data-testid="mobile-cta-button"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-brand-mint text-brand-navy-darker rounded-full px-5 py-3 font-semibold"
              >
                Book Consultation <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
